<template>
  <Dialog :open="open" @update:open="handleClose" title="Create New API Key" size="lg">
    <div class="w-full space-y-6" data-testid="create-api-key-modal">
      <!-- Success State -->
      <div v-if="createdKey" class="space-y-6">
        <div class="bg-green-50 border border-green-200 rounded-md p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-green-800">API Key Created!</h3>
              <p class="mt-1 text-sm text-green-700">Copy it now -- you won't see it again.</p>
            </div>
          </div>
        </div>

        <div class="space-y-3">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <p class="text-sm text-gray-900">{{ createdKey.name }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">API Key</label>
            <div class="flex items-center space-x-2">
              <code class="flex-1 bg-gray-100 border border-gray-300 rounded-md px-3 py-2 text-sm font-mono text-gray-900 select-all break-all">
                {{ createdKey.key }}
              </code>
              <Button variant="outline" size="sm" @click="copyKey" class="flex-shrink-0">
                {{ copied ? 'Copied!' : 'Copy' }}
              </Button>
            </div>
          </div>
          <div v-if="createdKey.expires_at">
            <label class="block text-sm font-medium text-gray-700 mb-1">Expires</label>
            <p class="text-sm text-gray-900">{{ new Date(createdKey.expires_at).toLocaleDateString() }}</p>
          </div>
        </div>
      </div>

      <!-- Create Form -->
      <form v-else @submit.prevent="handleSubmit" class="space-y-6">
        <!-- Name -->
        <div>
          <label for="name" class="block text-sm font-medium text-gray-700 mb-1">
            Name <span class="text-red-500">*</span>
          </label>
          <Input id="name" v-model="form.name" type="text" placeholder="e.g., Production API Key" :error="errors.name" class="w-full" required data-testid="key-name-input" />
        </div>

        <!-- Key Type -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Key Type <span class="text-red-500">*</span>
          </label>
          <div class="space-y-3">
            <div class="flex items-start">
              <Radio id="type-scope" v-model="form.key_type" value="scope" name="key_type" class="mt-0.5" />
              <div class="ml-3 text-sm">
                <label for="type-scope" class="font-medium text-gray-700">Scope-based</label>
                <p class="text-gray-500">Predefined permission set. Best for most use cases.</p>
              </div>
            </div>
            <div class="flex items-start">
              <Radio id="type-fg" v-model="form.key_type" value="fine_grained" name="key_type" class="mt-0.5" />
              <div class="ml-3 text-sm">
                <label for="type-fg" class="font-medium text-gray-700">Fine-grained</label>
                <p class="text-gray-500">Select individual permissions. For advanced use cases.</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Scope selector (scope-based keys) -->
        <div v-if="form.key_type === 'scope'">
          <label class="block text-sm font-medium text-gray-700 mb-2">Scope <span class="text-red-500">*</span></label>
          <div class="space-y-2">
            <div v-for="(label, scope) in scopeLabels" :key="scope" class="flex items-start">
              <Radio :id="`scope-${scope}`" v-model="form.scope" :value="scope" name="scope" class="mt-0.5" />
              <div class="ml-3 text-sm">
                <label :for="`scope-${scope}`" class="font-medium text-gray-700">{{ label }}</label>
                <p class="text-gray-500 text-xs">{{ scopePermissions[scope] }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Permission checkboxes (fine-grained keys) -->
        <div v-if="form.key_type === 'fine_grained'">
          <label class="block text-sm font-medium text-gray-700 mb-2">Permissions <span class="text-red-500">*</span></label>
          <div class="grid grid-cols-2 gap-2">
            <div v-for="perm in allPermissions" :key="perm" class="flex items-center">
              <input :id="`perm-${perm}`" v-model="selectedPermissions" :value="perm" type="checkbox" class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded" />
              <label :for="`perm-${perm}`" class="ml-2 text-sm text-gray-700 font-mono">{{ perm }}</label>
            </div>
          </div>
          <p v-if="errors.permissions" class="mt-1 text-sm text-red-600">{{ errors.permissions }}</p>
        </div>

        <!-- Expiration -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Expiration</label>
          <div class="space-y-3">
            <Checkbox v-model="form.never_expires" label="Never expires" />
            <div v-if="!form.never_expires">
              <Input id="expires-in-days" v-model.number="form.expires_in_days" type="number" class="w-full" min="1" max="3650" placeholder="365" />
              <p class="mt-1 text-xs text-gray-500">Days until expiration (1-3650)</p>
            </div>
          </div>
        </div>

        <!-- Security warning -->
        <div class="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3 text-sm text-yellow-700">
              <p>Your API key will only be shown once after creation. Store it securely.</p>
            </div>
          </div>
        </div>
      </form>
    </div>

    <template #footer>
      <div v-if="createdKey" class="sm:flex sm:flex-row-reverse">
        <Button @click="handleClose" class="w-full sm:w-auto">Done</Button>
      </div>
      <div v-else class="sm:flex sm:flex-row-reverse">
        <Button type="submit" @click="handleSubmit" :loading="isLoading" :disabled="!isFormValid" class="w-full sm:ml-3 sm:w-auto" data-testid="create-submit-button">
          Create API Key
        </Button>
        <Button variant="outline" @click="handleClose" :disabled="isLoading" class="mt-3 w-full sm:mt-0 sm:w-auto">
          Cancel
        </Button>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { Dialog, Button, Input, Radio, Checkbox, useNotification } from '@asabdev/asab-ui';
  import type {
    CreateApiKeyFormData,
    CreateApiKeyResponse,
    CreateApiKeyData,
    Permission,
  } from '@/features/api-keys/types';
  import { SCOPE_LABELS, SCOPE_PERMISSIONS } from '@/features/api-keys/types';
  import { apiKeysApi } from '@/lib/api/apiKeys';
  import { useOrganizationsStore } from '@/stores/organizations';

  interface Props {
    open: boolean;
  }

  interface Emits {
    (e: 'update:open', value: boolean): void;
    (e: 'created', response: CreateApiKeyResponse): void;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<Emits>();

  const orgStore = useOrganizationsStore();
  const { showSuccess, showError } = useNotification();

  const isLoading = ref(false);
  const createdKey = ref<CreateApiKeyResponse | null>(null);
  const copied = ref(false);
  const errors = ref<Record<string, string>>({});
  const selectedPermissions = ref<Permission[]>([]);

  const scopeLabels = SCOPE_LABELS;
  const scopePermissions = SCOPE_PERMISSIONS;

  const allPermissions: Permission[] = [
    'agents:read', 'agents:write', 'agents:execute',
    'kernel:read', 'kernel:write',
    'tools:callback',
    'orgs:read', 'orgs:write', 'orgs:delete',
    'members:read', 'members:write',
    'api_keys:manage',
    'sso:read', 'sso:manage',
    'billing:read', 'billing:write',
    'models:read', 'models:execute',
  ];

  const form = ref<CreateApiKeyFormData>({
    name: '',
    key_type: 'scope',
    scope: 'agent',
    permissions: [],
    expires_in_days: undefined,
    never_expires: false,
  });

  const isFormValid = computed(() => {
    if (form.value.name.trim().length < 3) return false;
    if (form.value.key_type === 'scope' && !form.value.scope) return false;
    if (form.value.key_type === 'fine_grained' && selectedPermissions.value.length === 0) return false;
    if (!form.value.never_expires && (!form.value.expires_in_days || form.value.expires_in_days <= 0)) return false;
    return true;
  });

  watch(() => props.open, (isOpen) => {
    if (isOpen) {
      form.value = { name: '', key_type: 'scope', scope: 'agent', permissions: [], expires_in_days: undefined, never_expires: false };
      selectedPermissions.value = [];
      errors.value = {};
      createdKey.value = null;
      copied.value = false;
    }
  });

  const handleSubmit = async () => {
    errors.value = {};
    if (form.value.name.trim().length < 3) {
      errors.value.name = 'Name must be at least 3 characters';
      return;
    }

    const orgId = orgStore.currentOrganizationId;
    if (!orgId) {
      showError('No organization selected');
      return;
    }

    isLoading.value = true;
    try {
      const data: CreateApiKeyData = { name: form.value.name.trim() };

      if (form.value.key_type === 'scope') {
        data.scope = form.value.scope;
      } else {
        data.permissions = selectedPermissions.value;
      }

      if (!form.value.never_expires && form.value.expires_in_days) {
        data.expires_in_days = form.value.expires_in_days;
      }

      const result = await apiKeysApi.createApiKey(orgId, data);
      createdKey.value = result;
      emit('created', result);
      showSuccess('API key created');
    } catch (error) {
      showError((error as Error).message || 'Failed to create API key');
    } finally {
      isLoading.value = false;
    }
  };

  const copyKey = async () => {
    if (!createdKey.value) return;
    try {
      await navigator.clipboard.writeText(createdKey.value.key);
      copied.value = true;
      showSuccess('Copied to clipboard');
      setTimeout(() => { copied.value = false; }, 2000);
    } catch {
      showError('Failed to copy');
    }
  };

  const handleClose = () => {
    emit('update:open', false);
  };
</script>
